import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { ClipCategory, ClipLabelVocabulary } from 'services/VideoEditingService';
import { humanize } from '../format';
import { Draft } from './ClipLabelFields';

/**
 * A clip label at a glance, read-only.
 *
 * Renders from a **`Draft`**, not a saved label, so a surface holding unsaved edits (the B-Roll
 * Library's Edit modal, which edits in a nested dialog) shows them immediately. Shared with the
 * Video Editing labels panel so there is one definition of what a label looks like rather than
 * two that drift.
 */

// How sure the MODEL was about its product guess. Absent once a person has saved the label — see
// `humanLabeled` below.
const CONFIDENCE_COLOR: Record<string, string> = {
  high: '#059669',
  medium: '#0284C7',
  low: '#D97706',
  none: '#64748B',
};

export function LabelRow({ children, title }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 0.75 }}>
      <Typography variant="caption" sx={{ width: 76, flexShrink: 0, color: '#64748B', pt: 0.25 }}>{title}</Typography>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

function Chips({ values, sx }: { values: string[]; sx?: object }) {
  if (!values.length) return <Typography variant="caption" color="text.secondary">—</Typography>;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {values.map(v => <Chip key={v} size="small" label={humanize(v)} sx={{ height: 22, fontSize: 11, ...sx }} />)}
    </Box>
  );
}

// A category's display name, falling back to the slug for one that has since been archived
// (the API omits those from the picker, but a clip filed under it still shows it).
export function categoryNameOf(categories: ClipCategory[], slug: string) {
  return categories.find(c => c.slug === slug)?.name || slug;
}

interface ClipLabelSummaryProps {
  draft: Draft;
  vocabulary: ClipLabelVocabulary | null;
  // Omitted hides the category row, matching ClipLabelFields.
  categories?: ClipCategory[] | null;
  /**
   * True once a person has saved this label (`labelSource === 'human'`). The model's confidence
   * is then replaced by a "human labeled" chip: it described the labeler's certainty about its
   * own product guess, which stops being the interesting fact the moment someone has vetted it.
   */
  humanLabeled?: boolean;
}

export default function ClipLabelSummary({
  draft, vocabulary, categories, humanLabeled,
}: ClipLabelSummaryProps) {
  const products = vocabulary?.products || [];
  const productName = products.find(p => p.handle === draft.productHandle)?.name || null;
  const confidence = draft.productConfidence || 'none';

  return (
    <Box>
      {draft.name && (
        <LabelRow title="Name">
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>{draft.name}</Typography>
        </LabelRow>
      )}
      {categories && (
        <LabelRow title="Categories">
          {draft.categories.length ? (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {draft.categories.map(slug => (
                <Chip
                  key={slug}
                  size="small"
                  label={categoryNameOf(categories, slug)}
                  sx={{ height: 22, fontSize: 11, backgroundColor: '#FEF3C7', color: '#92400E' }}
                />
              ))}
            </Box>
          ) : <Typography variant="caption" color="text.secondary">—</Typography>}
        </LabelRow>
      )}
      <LabelRow title="Product">
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
              {draft.productCategory && draft.productCategory !== 'none'
                ? humanize(draft.productCategory)
                : 'No product on screen'}
            </Typography>
            {humanLabeled ? (
              <Chip
                size="small"
                label="human labeled"
                variant="outlined"
                sx={{ height: 20, fontSize: 11, color: '#4F46E5', borderColor: '#4F46E5' }}
              />
            ) : (
              <Chip
                size="small"
                label={confidence}
                variant="outlined"
                sx={{ height: 20, fontSize: 11, color: CONFIDENCE_COLOR[confidence], borderColor: CONFIDENCE_COLOR[confidence] }}
              />
            )}
          </Box>
          {/* The exact SKU, only once someone has pinned one. */}
          {(productName || draft.productHandle) && (
            <Typography variant="caption" color="text.secondary">
              {productName || draft.productHandle}
            </Typography>
          )}
        </Box>
      </LabelRow>
      {/* Type is deliberately not shown — the classification never described anything an editor
          searches by. Still stored, and still read server-side by the b-roll talking rule. */}
      <LabelRow title="Shows / does">
        <Chips values={draft.clipSubject} sx={{ backgroundColor: '#EEF2FF', color: '#4F46E5' }} />
      </LabelRow>
      <LabelRow title="Keywords">
        {draft.keywords.length ? (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {draft.keywords.map(k => (
              <Chip key={k} size="small" label={k} variant="outlined" sx={{ height: 22, fontSize: 11 }} />
            ))}
          </Box>
        ) : <Typography variant="caption" color="text.secondary">—</Typography>}
      </LabelRow>
      {draft.description && (
        <LabelRow title="Describes">
          <Typography variant="body2" color="text.secondary">{draft.description}</Typography>
        </LabelRow>
      )}
      {draft.searchNotes && (
        <LabelRow title="Notes">
          <Typography variant="caption" color="text.secondary">{draft.searchNotes}</Typography>
        </LabelRow>
      )}
    </Box>
  );
}
